import { useMemo } from "react";
import {
  Star,
  AlertCircle,
  TrendingUp,
  Clock3,
  CheckCircle,
  Sparkles,
  MessageSquare,
} from "lucide-react";

import OverviewStatCard from "../../components/dashboard/production/OverviewStatCard";
import NeedsAttentionCard from "../../components/dashboard/production/NeedsAttentionCard";
import RecentActivityRail from "../../components/dashboard/production/RecentActivityRail";
import OverviewInsights from "../../components/dashboard/production/OverviewInsights";

const demoBusiness = {
  name: "Joe’s Coffee Shop",
};

const demoReviews = [
  {
    id: 1,
    authorName: "Sarah Mitchell",
    rating: 5,
    reviewDate: new Date(Date.now() - 1000 * 60 * 8).toISOString(),
    content:
      "Absolutely love this place! The staff was friendly, the service was fast, and everything exceeded my expectations.",
    suggestedResponse:
      "Thank you so much, Sarah! We’re thrilled to hear you had such a great experience with our team. We appreciate your support and hope to see you again soon!",
    responseText: "",
    status: "draft_generated",
  },
  {
    id: 2,
    authorName: "Chris Parker",
    rating: 3,
    reviewDate: new Date(Date.now() - 1000 * 60 * 34).toISOString(),
    content:
      "Great experience overall, but communication could have been better. I had to wait longer than expected.",
    suggestedResponse:
      "Thank you for your feedback, Chris. We’re glad you had a good overall experience, and we’re sorry the communication and wait time didn’t meet expectations. We appreciate you sharing this with us.",
    responseText: "",
    status: "needs_approval",
  },
  {
    id: 3,
    authorName: "Amanda Lewis",
    rating: 5,
    reviewDate: new Date(Date.now() - 1000 * 60 * 72).toISOString(),
    content:
      "Amazing service. Everyone was helpful and the coffee was perfect. This is definitely becoming my new regular spot.",
    suggestedResponse:
      "Thank you so much, Amanda! We’re so happy to hear you enjoyed the service and coffee. We look forward to seeing you again soon!",
    responseText:
      "Thank you so much, Amanda! We’re so happy to hear you enjoyed the service and coffee. We look forward to seeing you again soon!",
    status: "posted",
  },
  {
    id: 4,
    authorName: "Michael Johnson",
    rating: 4,
    reviewDate: new Date(Date.now() - 1000 * 60 * 115).toISOString(),
    content:
      "Really nice atmosphere and great drinks. It was a little crowded, but the team handled it well.",
    suggestedResponse:
      "Thank you, Michael! We’re glad you enjoyed the atmosphere and drinks. We appreciate your patience during a busy time.",
    responseText: "",
    status: "draft_generated",
  },
  {
    id: 5,
    authorName: "Nina Patel",
    rating: 5,
    reviewDate: new Date(Date.now() - 1000 * 60 * 190).toISOString(),
    content:
      "The team was so welcoming and the latte was excellent. Highly recommend stopping in.",
    suggestedResponse:
      "Thank you, Nina! We’re thrilled you enjoyed your latte and felt welcomed by our team.",
    responseText:
      "Thank you, Nina! We’re thrilled you enjoyed your latte and felt welcomed by our team.",
    status: "posted",
  },
  {
    id: 6,
    authorName: "David Brooks",
    rating: 2,
    reviewDate: new Date(Date.now() - 1000 * 60 * 260).toISOString(),
    content:
      "My order was incorrect and it took a while to fix. The staff was polite, but the experience was frustrating.",
    suggestedResponse:
      "Thank you for letting us know, David. We’re sorry your order was incorrect and that it took longer than expected to make right. We appreciate your patience and will use this feedback to improve.",
    responseText: "",
    status: "needs_approval",
  },
  {
    id: 7,
    authorName: "Emily Carter",
    rating: 5,
    reviewDate: new Date(Date.now() - 1000 * 60 * 360).toISOString(),
    content:
      "Great coffee, clean space, and friendly people. Exactly what I was hoping for.",
    suggestedResponse:
      "Thank you, Emily! We’re so glad you enjoyed the coffee, space, and service.",
    responseText:
      "Thank you, Emily! We’re so glad you enjoyed the coffee, space, and service.",
    status: "posted",
  },
];

export default function DemoOverview() {
  const reviews = demoReviews;

  const stats = useMemo(() => {
    const totalReviews = 248;
    const averageRating = "4.7";
    const unansweredReviews = reviews.filter(
      (review) => !review.responseText && review.status !== "posted",
    ).length;

    const respondedReviews = 228;
    const responseRate = 92;

    const pendingApproval = reviews.filter((review) =>
      ["draft_generated", "needs_approval", "pending_response"].includes(
        review.status,
      ),
    ).length;

    const positive = 198;
    const neutral = 31;
    const negative = 19;

    const reputationScore = 91;

    return {
      totalReviews,
      averageRating,
      unansweredReviews,
      respondedReviews,
      responseRate,
      pendingApproval,
      positive,
      neutral,
      negative,
      reputationScore,
    };
  }, [reviews]);

  const sentimentData = useMemo(() => {
    return [
      { name: "Positive", value: stats.positive, fill: "#4f46e5" },
      { name: "Neutral", value: stats.neutral, fill: "#94a3b8" },
      { name: "Negative", value: stats.negative, fill: "#f97316" },
    ].filter((item) => item.value > 0);
  }, [stats.positive, stats.neutral, stats.negative]);

  const needsAttention = useMemo(() => {
    return reviews
      .filter(
        (review) =>
          !review.responseText ||
          review.status === "draft_generated" ||
          review.status === "needs_approval" ||
          Number(review.rating) <= 3,
      )
      .slice(0, 3);
  }, [reviews]);

  const activity = [
    {
      icon: Sparkles,
      title: "Response prepared",
      description: "Sarah Mitchell · 5-star review",
      time: "2 min ago",
    },
    {
      icon: CheckCircle,
      title: "Response approved",
      description: "Amanda Lewis response posted to Google",
      time: "11 min ago",
    },
    {
      icon: AlertCircle,
      title: "Review needs attention",
      description: "2-star review from David Brooks",
      time: "28 min ago",
    },
    {
      icon: MessageSquare,
      title: "New review synced",
      description: "Michael Johnson left a 4-star review",
      time: "1 hr ago",
    },
    {
      icon: Sparkles,
      title: "Response prepared",
      description: "Chris Parker review awaiting approval",
      time: "2 hr ago",
    },
    {
      icon: CheckCircle,
      title: "Response posted",
      description: "Nina Patel review completed",
      time: "Today",
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          Overview
        </h1>

        <p className="text-slate-500 mt-1">
          Monitor reputation health and recent activity for{" "}
          <span className="font-medium text-slate-700">
            {demoBusiness.name}
          </span>
          .
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <OverviewStatCard
          title="Overall Rating"
          value={stats.averageRating}
          subtitle={`Based on ${stats.totalReviews} reviews`}
          icon={<Star size={22} className="text-indigo-600" />}
          iconBg="bg-indigo-100"
        />

        <OverviewStatCard
          title="Needs Attention"
          value={stats.unansweredReviews}
          subtitle="3 waiting approval"
          icon={<AlertCircle size={22} className="text-orange-600" />}
          iconBg="bg-orange-100"
        />

        <OverviewStatCard
          title="Response Rate"
          value={`${stats.responseRate}%`}
          subtitle="+8% this month"
          icon={<TrendingUp size={22} className="text-blue-600" />}
          iconBg="bg-blue-100"
        />

        <OverviewStatCard
          title="Avg Response Time"
          value="2.4h"
          subtitle="-1.3h this month"
          icon={<Clock3 size={22} className="text-purple-600" />}
          iconBg="bg-purple-100"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 xl:items-start">
        <div className="xl:col-span-2 space-y-6">
          <NeedsAttentionCard
            reviews={needsAttention}
            pendingCount={needsAttention.length}
            loading={false}
          />

          <OverviewInsights
            reputationScore={stats.reputationScore}
            sentimentData={sentimentData}
          />
        </div>

        <RecentActivityRail activity={activity} loading={false} />
      </div>
    </div>
  );
}
